import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatSnackBar} from "@angular/material/snack-bar";
import {Router, RouterOutlet} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {OAuthService} from 'angular-oauth2-oidc';
import {EMPTY, switchMap, take, tap} from 'rxjs';
import {filter} from "rxjs/operators";
import {User} from '../api/models/user';
import {RoleTopicService} from "../api/ws/services/role-topic.service";
import {HeaderComponent} from './header/header.component';
import {routeOfExpectedValidationNotification} from "./in-app-notification/route-expected-validation-notification.pipe";
import {LoadingIndicatorComponent} from "./loading/loading-indicator/loading-indicator.component";
import {authConfig} from './oidc/auth.config';
import {NotificationStore} from "./state/notification.store";
import {UserStore} from './state/user.store';

@Component({
  selector: 'euroports-root',
  imports: [RouterOutlet, HeaderComponent, MatProgressSpinner, LoadingIndicatorComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private oauthService = inject(OAuthService);
  private userStore = inject(UserStore);
  private notificationStore = inject(NotificationStore);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private snackbar = inject(MatSnackBar);
  private roleTopicService = inject(RoleTopicService);
  private destroyRef = inject(DestroyRef);

  loggedUser = this.userStore.currentUser;
  user$ = toObservable(this.userStore.currentUser);

  constructor() {
    this.setupAuthConfig();
    this.setupLangConfig();
  }

  ngOnInit(): void {
    if (this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken()) {
      this.oauthService.loadDiscoveryDocumentAndTryLogin().then(
        (hasReceivedTokens) => {
          if (hasReceivedTokens) {
            this.userStore.loadUser();
            this.userStore.loadScopes();
          }
        }
      );
    } else {
      this.oauthService.loadDiscoveryDocumentAndLogin({ state: window.location.pathname }).then(
        () => {
          if (this.oauthService.hasValidAccessToken()) {
            this.userStore.loadUser();
            this.userStore.loadScopes();
            const stateUrl = decodeURIComponent(this.oauthService.state!);
            void this.router.navigate([stateUrl]);
          }
        }
      );
    }

    this.user$.pipe(
      filter(user => this.hasServicesOrRoles(user)),
      take(1),
      switchMap(user => this.roleTopicService.createRoleTopic(user)),
      tap(() => this.notificationStore.loadNotificationsCount()),
      switchMap(event => {
        if (event.type === 'VALIDATION_EXPECTED') {
          const snackbarRef = this.snackbar.open(
            this.translate.instant('notifications.validationRequest'),
            this.translate.instant('notifications.goto')
          );
          return snackbarRef.onAction().pipe(
            tap(() => this.router.navigate(routeOfExpectedValidationNotification(event)))
          );
        }
        return EMPTY;
      }),
      // better not open websocket if app.component is destroyed during initialization
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  private hasServicesOrRoles(user: User | undefined): user is User {
    if (!user) {
      return false;
    }
    return !!user.services || !!user.roles;
  }

  private setupAuthConfig() {
    this.oauthService.setStorage(sessionStorage);
    this.oauthService.configure(authConfig);
    this.oauthService.setupAutomaticSilentRefresh();

    this.oauthService.events
      .pipe(
        filter(e => e.type === 'token_refresh_error' || e.type === 'token_error'),
        takeUntilDestroyed(this.destroyRef),)
      .subscribe(() => {
        this.oauthService.initLoginFlow();
      });

  }

  private setupLangConfig() {
    this.translate.addLangs(['fr', 'nl', 'en']);
    this.translate.setFallbackLang('fr');
    this.translate.use('fr');
  }
}
